import React, { useCallback, useState, useEffect } from 'react'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import InfoIcon from '@mui/icons-material/Info'
import { Dialog } from 'primereact/dialog'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { horsesState } from '../../../../state/Horses.state'
import { appState } from '../../../../state/App.state'
import { Loading } from '../../../loading/Loading'
import { useLocalState } from '../../../../shared/UseLocalState'
import { BookedService, Horse, ServiceDto, Starter } from '../../../../api'
import { format } from 'date-fns'
import { registrationState } from '../Registration.state'
import { formatDate } from '../../../../shared/format-date'
import { ServiceSelectionPanel } from './ServiceSelectionPanel'
import { actualShowState } from '../../../../state/ActualShow.state'

export const HorsesScreen = () => {
  /* STATE */
  const horses = useLocalState<Horse[]>(horsesState.horses$, horsesState.horses$.value)
  const selectedHorse = useLocalState<Horse | null>(registrationState.selectedHorse$, registrationState.selectedHorse$.value)
  const [detail, setDetail] = useState<Horse | null>()
  const [regId, setRegId] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const additionalHorses = useLocalState<Horse[]>(registrationState.additionalHorses$, registrationState.additionalHorses$.value)
  const selectedStarter = useLocalState<Starter | null>(registrationState.selectedStarter$, registrationState.selectedStarter$.value)
  const actualShowBookedServices = useLocalState(actualShowState.actualShowBookedServices$, actualShowState.actualShowBookedServices$.value)
  const showServices = useLocalState<ServiceDto[]>(actualShowState.actualShowServices$, actualShowState.actualShowServices$.value)

  /* DERIVED STATE */
  const bookedHorseServices = React.useMemo(
    () =>
      actualShowBookedServices?.filter((bs) => {
        const svcType = showServices?.find((so) => so?.id === bs?.serviceId)?.type
        return (svcType === 'H' || svcType === 'I') && bs?.horseId === selectedHorse?.registerId
      }),
    [actualShowBookedServices, showServices, selectedHorse]
  )

  /* LOGIC */
  const getHorseByRegId = useCallback(() => {
    if ([...horses, ...additionalHorses]?.some((horse) => horse?.registerId === regId))
      return appState.addToast('Pferd bereits in Tabelle!', 'warning')
    setLoading(true)
    horsesState
      .fetchHorseById(regId)
      .then((horse) => {
        appState.addToast('Pferd gefunden: ' + horse?.name, 'info')
        registrationState.additionalHorses$.next([...registrationState.additionalHorses$.value, horse])
        registrationState.selectedHorse$.next(horse)
      })
      .catch(() => {
        appState.addToast('Pferd mit RegId ' + regId + ' nicht gefunden', 'error')
      })
      .finally(() => setLoading(false))
  }, [regId, horses, additionalHorses])

  // Synchronisiere Pferd-Auswahl bei Starter-Wechsel oder wenn additionalHorses nachladen
  useEffect(() => {
    if (!selectedStarter) return
    const horseId = selectedStarter.horseRegisterId
    const allHorses = [...horses, ...additionalHorses]
    const found = allHorses.find((h) => h?.registerId === horseId)
    if (found) {
      registrationState.selectedHorse$.next(found)
    }
  }, [selectedStarter, additionalHorses, horses])

  /* TEMPLATE */
  return (
    <>
      <Loading loading={loading} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        <div>
          <Dialog
            visible={!!detail}
            resizable={false}
            draggable={false}
            style={{ maxWidth: '92vw' }}
            header={detail?.name}
            onHide={() => setDetail(null)}
            baseZIndex={99999999999999}
          >
            <DataTable
              showHeaders={false}
              value={[
                { label: 'Geburtstag', value: detail?.birthday && formatDate(detail?.birthday) },
                { label: 'Rasse', value: detail?.breed },
                { label: 'Farbe', value: detail?.color },
                { label: 'Stockmaß', value: detail?.stockSize != null ? `${detail.stockSize} cm` : undefined },
                { label: 'Großpferd', value: detail?.isBigHorse ? 'Ja' : 'Nein' },
                { label: 'Mutter', value: detail?.mother },
                { label: 'Vater', value: detail?.father },
                { label: 'RegistrierId', value: detail?.registerId }
              ]}
            >
              <Column field='label' body={({ label }) => <b>{label}</b>} />
              <Column field='value' />
            </DataTable>
          </Dialog>
          <DataTable
            showSelectAll={false}
            showHeaders={false}
            value={[...horses, ...additionalHorses]}
            selectionMode={'single'}
            selection={selectedHorse}
            onSelectionChange={(e) => registrationState.selectedHorse$.next(e.value)}
            isDataSelectable={() => !selectedStarter}
          >
            <Column selectionMode='single' bodyStyle={{ width: '3rem' }} />
            <Column
              body={(horse: Horse) => (
                <div>
                  <p style={{ marginBottom: '0.5rem' }}>{horse?.name}</p>
                  <p>{horse?.birthday && format(new Date(horse.birthday), 'dd.MM.yyyy')}</p>
                </div>
              )}
            />
            <Column
              headerStyle={{ width: '3rem' }}
              body={(horse: Horse) => (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <InfoIcon
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      setDetail(horse)
                    }}
                  />
                </div>
              )}
            />
          </DataTable>
        </div>
        <div>
          <p style={{ marginBottom: '0.5rem' }}>
            <b>Pferd über Registrierungs ID hinzufügen</b>
          </p>
          <div style={{ display: 'flex', marginBottom: '0.5rem' }}>
            <InputText
              value={regId}
              onChange={(e) => setRegId(e?.target?.value ?? '')}
              style={{ flex: 1 }}
              placeholder='Registrierungs ID eingeben ...'
              onKeyDown={(e) => {
                if (e.key === 'Enter' && regId?.length > 1) getHorseByRegId()
              }}
            />
            <Button onClick={getHorseByRegId} disabled={regId?.length < 1} label='Suchen' />
          </div>
          <div style={{ display: 'flex' }}>
            <a href='#/horses' style={{ flex: 1 }}>
              <Button style={{ width: '100%', textAlign: 'center' }} outlined label='Neues Pferd registrieren' />
            </a>
          </div>
        </div>
      </div>

      {/* Schon gebuchte Services H/I des ausgewählten Pferdes */}
      {bookedHorseServices?.length > 0 && (
        <div style={{ marginTop: '1.5rem' }}>
          <h3>Gebuchte Services für {selectedHorse?.name}</h3>
          <DataTable value={bookedHorseServices} style={{ width: '100%' }}>
            <Column
              header='Service'
              body={(service: BookedService) => showServices?.find((s) => s?.id === service?.serviceId)?.name}
            />
            <Column
              header='Menge'
              headerStyle={{ width: '4rem', textAlign: 'right' }}
              bodyStyle={{ width: '4rem', textAlign: 'right' }}
              body={(service: BookedService) => service?.quantity}
            />
            <Column
              header='Preis'
              headerStyle={{ width: '5rem', textAlign: 'right' }}
              bodyStyle={{ width: '5rem', textAlign: 'right', whiteSpace: 'nowrap' }}
              body={(service: BookedService) => service?.totalPrice?.toFixed(2).replace('.', ',') + ' €'}
            />
          </DataTable>
        </div>
      )}

      {/* Services Typ I und H wählbar – Impfung und Unterkunft Pferd */}
      <ServiceSelectionPanel types={['I', 'H']} title='Services für das Pferd:' disabled={!!selectedStarter} />
    </>
  )
}
